import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff, Users, Calendar } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Bitte fuellen Sie alle Felder aus');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post('/auth/login', formData);
      const { access_token, user } = response.data;
      onLogin(user, access_token);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleHostessLogin = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post('/auth/hostess-login');
      const { access_token, user } = response.data;
      onLogin(user, access_token, true); // true = isHostess flag
    } catch (error) {
      console.error('Hostess login error:', error);
      toast.error('Hostess-Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-amber-200/30 to-orange-300/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-red-200/30 to-orange-300/30 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md p-6 relative z-10">
        <div className="text-center mb-8 animate-slide-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-4 shadow-lg">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold font-space-grotesk text-gray-900 mb-2">
            VIP Tischplan
          </h1>
          <p className="text-gray-600">
            Professionelle Event-Verwaltung und Check-In System
          </p>
        </div>

        <Card className="card-glass animate-scale-in" data-testid="login-card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-semibold text-gray-900">
              Anmeldung
            </CardTitle>
            <CardDescription className="text-gray-600">
              Melden Sie sich mit Ihren Zugangsdaten an
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  E-Mail-Adresse
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="ihre.email@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full transition-all duration-200 focus:ring-2 focus:ring-amber-500/20"
                  data-testid="email-input"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Passwort
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Ihr Passwort"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pr-10 transition-all duration-200 focus:ring-2 focus:ring-amber-500/20"
                    data-testid="password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-medium py-2.5 rounded-lg transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Anmeldung laeuft...
                  </div>
                ) : (
                  'Anmelden'
                )}
              </Button>
              
              {/* Hostess Login Button */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">oder</span>
                </div>
              </div>
              
              <Button
                type="button"
                onClick={handleHostessLogin}
                disabled={loading}
                variant="outline"
                className="w-full border-2 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 font-medium py-2.5 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="hostess-login-button"
              >
                Als Hostess einloggen
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>Event-Management & Check-In System</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-sm text-gray-500 animate-fade-in">
          <p>
            Fuer Admin-Zugang wenden Sie sich an den Systemadministrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;